import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { S, PRIMARY } from './styles'
import { renderText } from './utils'
import DataTable from './DataTable'
import { API_URL } from '../../config'

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function DatePickerForm({ datePicker, onOptionClick }) {
  const now = new Date()
  const today = now.toISOString().slice(0, 10)
  const isMonthYear =
    datePicker.dateParamStyle === 'monthYear' || datePicker.dateParamStyle === 'monthYear_leave'

  // date range state
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  // month+year state
  const [month, setMonth] = useState(String(now.getMonth() + 1))
  const [year, setYear] = useState(String(now.getFullYear()))
  const [error, setError] = useState('')

  function handleConfirm() {
    if (isMonthYear) {
      if (!month || !year) {
        setError('Please select a month and year.')
        return
      }
      setError('')
      // derive synthetic startDate/endDate for display (first/last day of month)
      const m = parseInt(month, 10)
      const y = parseInt(year, 10)
      const firstDay = `${y}-${String(m).padStart(2, '0')}-01`
      const lastDay = new Date(y, m, 0)
      const lastDayStr = `${y}-${String(m).padStart(2, '0')}-${String(lastDay.getDate()).padStart(
        2,
        '0',
      )}`
      onOptionClick({
        type: 'report_date_confirm',
        path: datePicker.path,
        labelName: datePicker.labelName,
        sidebar: datePicker.sidebar,
        desc: datePicker.desc,
        exportExcel: datePicker.exportExcel,
        exportPdf: datePicker.exportPdf,
        dateParamStyle: datePicker.dateParamStyle,
        startDate: firstDay,
        endDate: lastDayStr,
        selectedMonth: month,
        selectedYear: year,
      })
    } else {
      if (!startDate || !endDate) {
        setError('Please select both start and end dates.')
        return
      }
      if (endDate < startDate) {
        setError('End date must be on or after start date.')
        return
      }
      setError('')
      onOptionClick({
        type: 'report_date_confirm',
        path: datePicker.path,
        labelName: datePicker.labelName,
        sidebar: datePicker.sidebar,
        desc: datePicker.desc,
        exportExcel: datePicker.exportExcel,
        exportPdf: datePicker.exportPdf,
        dateParamStyle: datePicker.dateParamStyle,
        startDate,
        endDate,
      })
    }
  }

  const inputStyle = {
    border: `1.5px solid ${PRIMARY}`,
    borderRadius: 8,
    padding: '5px 8px',
    fontSize: 12.5,
    outline: 'none',
    fontFamily: 'inherit',
    background: '#fff',
    color: '#222',
    width: '100%',
    boxSizing: 'border-box',
  }

  const currentYear = now.getFullYear()
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i)

  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {isMonthYear ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Month</label>
            <select
              value={month}
              onChange={(e) => {
                setMonth(e.target.value)
                setError('')
              }}
              style={inputStyle}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={String(i + 1)}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Year</label>
            <select
              value={year}
              onChange={(e) => {
                setYear(e.target.value)
                setError('')
              }}
              style={inputStyle}
            >
              {yearOptions.map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Start Date</label>
            <input
              type="date"
              max={today}
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value)
                setError('')
              }}
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>End Date</label>
            <input
              type="date"
              max={today}
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value)
                setError('')
              }}
              style={inputStyle}
            />
          </div>
        </>
      )}
      {error && <div style={{ fontSize: 11.5, color: '#c0392b' }}>{error}</div>}
      <button
        style={{ ...S.linkBtn, marginTop: 2, alignSelf: 'flex-start' }}
        onClick={handleConfirm}
      >
        Confirm ✓
      </button>
    </div>
  )
}

DatePickerForm.propTypes = {
  datePicker: PropTypes.shape({
    path: PropTypes.string.isRequired,
    labelName: PropTypes.string.isRequired,
    sidebar: PropTypes.string,
    desc: PropTypes.string,
    exportExcel: PropTypes.string,
    exportPdf: PropTypes.string,
    dateParamStyle: PropTypes.string,
  }).isRequired,
  onOptionClick: PropTypes.func.isRequired,
}

function DetailedLeaveFilterForm({ datePicker, onOptionClick, token }) {
  const now = new Date()
  const currentYear = now.getFullYear()
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i)

  const [month, setMonth] = useState(String(now.getMonth() + 1))
  const [year, setYear] = useState(String(currentYear))
  const [leaveTypes, setLeaveTypes] = useState([])
  const [branches, setBranches] = useState([])
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState([])
  const [selectedBranches, setSelectedBranches] = useState([])
  const [error, setError] = useState('')

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` }
    fetch(`${API_URL}/attendance/leave_types/`, { headers })
      .then((r) => r.json())
      .then((d) => setLeaveTypes(d.results || d))
      .catch(() => {})
    fetch(`${API_URL}/employee/branch/`, { headers })
      .then((r) => r.json())
      .then((d) => setBranches(d.results || d))
      .catch(() => {})
  }, [token])

  function toggleItem(id, list, setList) {
    setList((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  function handleConfirm() {
    if (!month || !year) {
      setError('Please select a month and year.')
      return
    }
    setError('')
    onOptionClick({
      type: 'report_date_confirm',
      path: datePicker.path,
      labelName: datePicker.labelName,
      sidebar: datePicker.sidebar,
      desc: datePicker.desc,
      exportExcel: datePicker.exportExcel,
      exportPdf: datePicker.exportPdf,
      dateParamStyle: datePicker.dateParamStyle,
      selectedMonth: month,
      selectedYear: year,
      leaveTypes: selectedLeaveTypes,
      branches: selectedBranches,
    })
  }

  const inputStyle = {
    border: `1.5px solid ${PRIMARY}`,
    borderRadius: 8,
    padding: '5px 8px',
    fontSize: 12.5,
    outline: 'none',
    fontFamily: 'inherit',
    background: '#fff',
    color: '#222',
    width: '100%',
    boxSizing: 'border-box',
  }

  const chipStyle = (active) => ({
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 11.5,
    cursor: 'pointer',
    border: `1.5px solid ${PRIMARY}`,
    background: active ? PRIMARY : 'transparent',
    color: active ? '#fff' : PRIMARY,
    margin: '2px 3px 2px 0',
  })

  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Year</label>
          <select value={year} onChange={(e) => setYear(e.target.value)} style={inputStyle}>
            {yearOptions.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Month</label>
          <select value={month} onChange={(e) => setMonth(e.target.value)} style={inputStyle}>
            {MONTHS.map((m, i) => (
              <option key={m} value={String(i + 1)}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>

      {leaveTypes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Leave Types</label>
          <div>
            {leaveTypes.map((lt) => (
              <span
                key={lt.id}
                style={chipStyle(selectedLeaveTypes.includes(lt.id))}
                onClick={() => toggleItem(lt.id, selectedLeaveTypes, setSelectedLeaveTypes)}
              >
                {lt.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {branches.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Departments</label>
          <div>
            {branches.map((b) => (
              <span
                key={b.id}
                style={chipStyle(selectedBranches.includes(b.id))}
                onClick={() => toggleItem(b.id, selectedBranches, setSelectedBranches)}
              >
                {b.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {error && <div style={{ fontSize: 11.5, color: '#c0392b' }}>{error}</div>}
      <button
        style={{ ...S.linkBtn, marginTop: 2, alignSelf: 'flex-start' }}
        onClick={handleConfirm}
      >
        Confirm ✓
      </button>
    </div>
  )
}

DetailedLeaveFilterForm.propTypes = {
  datePicker: PropTypes.shape({
    path: PropTypes.string.isRequired,
    labelName: PropTypes.string.isRequired,
    sidebar: PropTypes.string,
    desc: PropTypes.string,
    exportExcel: PropTypes.string,
    exportPdf: PropTypes.string,
    dateParamStyle: PropTypes.string,
  }).isRequired,
  onOptionClick: PropTypes.func.isRequired,
  token: PropTypes.string,
}

export default function Message({ msg, onNavigate, onOptionClick }) {
  const isUser = msg.role === 'user'
  if (isUser) {
    return <div style={S.msgUser}>{msg.text}</div>
  }
  return (
    <div style={S.msgBot}>
      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
        {msg.text.split('\n').map((line, i) => (
          <div key={i}>{renderText(line)}</div>
        ))}
      </div>
      {msg.data && <DataTable rows={msg.data} onNavigate={onNavigate} />}
      {msg.downloadFile && (
        <div style={{ marginTop: 8 }}>
          <a
            href={msg.downloadFile.url}
            download
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 16px',
              background: 'linear-gradient(145deg, #8fd44a 0%, #6ab52e 100%)',
              color: '#fff',
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            }}
          >
            ⬇ Download {msg.downloadFile.name}
          </a>
        </div>
      )}
      {msg.datePicker && msg.datePicker.dateParamStyle === 'detailedLeave' ? (
        <DetailedLeaveFilterForm
          datePicker={msg.datePicker}
          onOptionClick={onOptionClick}
          token={msg.datePicker.token}
        />
      ) : msg.datePicker ? (
        <DatePickerForm datePicker={msg.datePicker} onOptionClick={onOptionClick} />
      ) : null}
      {msg.link && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button style={{ ...S.linkBtn, marginTop: 0 }} onClick={() => onNavigate(msg.link)}>
            View →
          </button>
          <button
            style={{
              ...S.linkBtn,
              marginTop: 0,
              background: 'transparent',
              color: PRIMARY,
              border: `1.5px solid ${PRIMARY}`,
            }}
            onClick={() => window.open('/#' + msg.link, '_blank')}
          >
            Open in new tab ↗
          </button>
        </div>
      )}
      {msg.options && msg.options.length > 0 && (
        <div style={S.optionBtns}>
          {msg.options.map((opt, i) => (
            <button
              key={i}
              style={S.optionBtn}
              onClick={() => onOptionClick(opt)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = PRIMARY
                e.currentTarget.style.color = '#fff'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = PRIMARY
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

Message.propTypes = {
  msg: PropTypes.shape({
    role: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(PropTypes.object),
    link: PropTypes.string,
    datePicker: PropTypes.shape({
      path: PropTypes.string.isRequired,
      labelName: PropTypes.string.isRequired,
      sidebar: PropTypes.string,
      desc: PropTypes.string,
      exportExcel: PropTypes.string,
      exportPdf: PropTypes.string,
      dateParamStyle: PropTypes.string,
      token: PropTypes.string,
    }),
    downloadFile: PropTypes.shape({
      url: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        type: PropTypes.string,
        value: PropTypes.string,
        path: PropTypes.string,
        desc: PropTypes.string,
        sidebar: PropTypes.string,
      }),
    ),
  }).isRequired,
  onNavigate: PropTypes.func.isRequired,
  onOptionClick: PropTypes.func.isRequired,
}
